import { type FormEvent, type ReactElement, useMemo, useState } from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import { authApi } from '@/auth/api';
import { useApiErrorMessage } from '@/shared/data/errors';
import { formErrorAlertSx } from '@/shared/ui/formAlertStyles';
import { s } from '@/theme/tokens';
import { uiToneSeverityMap } from '@/theme/uiTokens';
import { Box, FormCard, FormSubmitButton, Inline, Section, Stack, Text } from '@/ui';
import {
  passwordPattern,
  passwordValidationMessage,
  requiredFieldMessage,
} from '@/ui/hooks/validation';
import { Alert, Link, TextField } from '@/ui/primitives';
import { PageShell } from '@/ui/primitives/PageShell';

type ResetPasswordConfirmPageProps = {
  session: { userId: string; orgId?: string } | null;
};

const invalidTokenMessage = 'This password reset link is invalid or expired.';

export const ResetPasswordConfirmPage = ({
  session,
}: ResetPasswordConfirmPageProps): ReactElement => {
  const { token } = useParams<{ token: string }>();
  const toErrorMessage = useApiErrorMessage();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fieldError = passwordError ?? confirmPasswordError;

  const resetToken = token?.trim() ?? '';
  const hasValidToken = resetToken.length >= 10;
  const canSubmit = useMemo(
    () => password.length > 0 && confirmPassword.length > 0 && !isSubmitting,
    [confirmPassword.length, isSubmitting, password.length],
  );

  if (session) {
    return <Navigate to="/account-settings" replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!hasValidToken) {
      setSubmitError(invalidTokenMessage);
      return;
    }
    if (!password) {
      setPasswordError(requiredFieldMessage);
      return;
    }
    if (!passwordPattern.test(password)) {
      setPasswordError(passwordValidationMessage);
      return;
    }
    if (!confirmPassword) {
      setConfirmPasswordError(requiredFieldMessage);
      return;
    }
    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }

    setSubmitError(null);
    setPasswordError(null);
    setConfirmPasswordError(null);
    setIsSubmitting(true);

    try {
      await authApi.resetPasswordConfirm({ token: resetToken, password });
      setSuccess(true);
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      const message = toErrorMessage(error);
      if (message.toLowerCase().includes('reset token')) {
        setSubmitError(invalidTokenMessage);
      } else {
        setSubmitError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell maxWidth="sm" paddingX="lg" paddingY="xl" paddingBottom="xl">
      <Stack gap="lg" sx={{ pt: { xs: s('xl'), sm: s('5xl') }, gap: { xs: s('lg'), sm: s('xl') } }}>
        <Section>
          <Stack gap="lg" alignItems="center">
            <Box
              component="form"
              onSubmit={(event) => void handleSubmit(event)}
              sx={{ width: '100%' }}
            >
              <FormCard
                title="Choose New Password"
                contentGap="sm"
                footer={
                  success ? (
                    <Inline gap="sm">
                      <Text role="body" color="textSecondary">
                        Password updated.
                      </Text>
                      <Link component={RouterLink} to="/sign-in" underline="hover" variant="body2">
                        Sign in
                      </Link>
                    </Inline>
                  ) : (
                    <FormSubmitButton
                      loading={isSubmitting}
                      disabled={!canSubmit}
                      fullWidth={false}
                      fullWidthOnMobile={false}
                      type="submit"
                      sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                    >
                      Update Password
                    </FormSubmitButton>
                  )
                }
              >
                {!hasValidToken ? (
                  <Alert severity="error" color="red">
                    {invalidTokenMessage}
                  </Alert>
                ) : null}
                {success ? (
                  <Alert
                    severity="success"
                    color="green"
                    variant="outlined"
                    sx={{
                      border: '1px solid',
                      borderColor: 'green.main',
                      color: 'green.main',
                      '& .MuiAlert-icon': { color: 'green.main' },
                    }}
                  >
                    Your password has been reset successfully.
                  </Alert>
                ) : null}
                {submitError ? (
                  <Alert severity="error" color="red">
                    {submitError}
                  </Alert>
                ) : null}
                {!success ? (
                  <>
                    <TextField
                      label="New Password"
                      labelGap="xs"
                      type="password"
                      value={password}
                      onChange={(event) => {
                        setPassword(event.target.value);
                        setPasswordError(null);
                      }}
                      fullWidth
                    />
                    <TextField
                      label="Confirm Password"
                      labelGap="xs"
                      type="password"
                      value={confirmPassword}
                      onChange={(event) => {
                        setConfirmPassword(event.target.value);
                        setConfirmPasswordError(null);
                      }}
                      fullWidth
                    />
                    {fieldError ? (
                      <Alert
                        severity={uiToneSeverityMap.red}
                        color="error"
                        variant="outlined"
                        sx={formErrorAlertSx}
                      >
                        {fieldError}
                      </Alert>
                    ) : null}
                  </>
                ) : null}
              </FormCard>
            </Box>
          </Stack>
        </Section>
      </Stack>
    </PageShell>
  );
};
